import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* ONE colleague, for the company admin — the screen behind a row on /company-members.

   It exists because the roster is a list you read and this is a page you act on. The seats a
   person holds, reclaiming one, making them an admin, taking them off the roster: each is a
   decision about that person, and putting all of it inline turned a roster of a few hundred
   names into a wall of buttons where the names were the hard part to find.

   Nothing here is new machinery — the same payload the list already built, for one id. The
   tags wear the colour and icon the company gave them on the roster's filter strip, which is
   also the only place they can be changed: a look edited from one person's screen would be a
   change to everybody's, made where only one of them is visible. */

const HEX = /^#[0-9a-f]{6}$/i;

// Mirrors the roster screen's chip: the tint is mixed here so a tag with no colour keeps the
// plain grey chip, untouched.
const mixWhite = (color, amount) => {
  const n = parseInt(color.slice(1), 16);
  const mix = (v) => Math.round(v + (255 - v) * amount);
  /* eslint-disable no-bitwise */
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
  /* eslint-enable no-bitwise */
};

const tagStyle = (color) => (HEX.test(color || '') ? {
  background: mixWhite(color, 0.88), borderColor: mixWhite(color, 0.6), color,
} : undefined);

const CompanyMemberPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { memberId } = useParams();
  const [member, setMember] = useState(null);
  const [selfId, setSelfId] = useState(null);
  // Only Bravos sells a membership and Lab seats; every other brand's roster has neither.
  const [isBravos, setIsBravos] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reclaiming, setReclaiming] = useState(null);
  const [notice, setNotice] = useState(null);
  const [adding, setAdding] = useState(false);
  const [tagValue, setTagValue] = useState('');
  // The company's whole tag vocabulary with its look — for the chips, and as the suggestions
  // behind the "+" so a tag is re-used rather than re-typed slightly differently.
  const [catalog, setCatalog] = useState([]);

  const load = useCallback(() => {
    apiRequest('GET', `/bravos/members/person/${memberId}`)
      .then((res) => {
        setMember(res.member);
        setSelfId(res.self_id);
        setIsBravos(res.is_bravos !== false);
        setCatalog(res.tags || []);
      })
      .catch(() => navigate('/company-members'))
      .finally(() => setLoading(false));
  }, [memberId, navigate]);

  useEffect(() => { load(); }, [load]);

  const setTags = (tags) => {
    if (busy) return;
    setBusy(true);
    apiRequest('POST', '/bravos/members/tags', { id: memberId, tags })
      .then(load)
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const commitTag = () => {
    const tag = tagValue.trim();
    const tags = (member && member.tags) || [];
    setTagValue('');
    setAdding(false);
    if (tag && !tags.includes(tag.toLowerCase())) setTags([...tags, tag]);
  };

  const makeAdmin = () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/make-admin', { partner_id: memberId })
      .then(() => { setNotice(t('adminGrantedNotice')); load(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  // The way back: company access ends, the person stays a colleague on the roster. Never the
  // company's own contact person (that is a change of contact person) and never yourself.
  const demoteAdmin = () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/demote', { partner_id: memberId })
      .then(() => { setNotice(t('adminDemotedNotice')); load(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  // Reclaiming puts the seat back in the company pool — the point of holding seats centrally.
  const reclaimSeat = (ticketId) => {
    if (reclaiming) return;
    setReclaiming(ticketId);
    setNotice(null);
    apiRequest('POST', '/bravos/reclaim-seat', { ticket_id: ticketId })
      .then(() => { setNotice(t('seatReclaimed')); load(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('cancelFailed') })))
      .finally(() => setReclaiming(null));
  };

  // "Remove" doesn't delete the person — the backend ends their employee/admin role and files
  // them as an internal 'affiliate', so they drop off this roster but stay known. Back to the
  // list afterwards: this screen is about somebody who is no longer on it.
  const removeMember = () => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('removeMemberConfirm', { name: member.name }))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/remove', { partner_id: memberId })
      .then(() => navigate('/company-members'))
      .catch((err) => {
        setNotice(t(err.message, { defaultValue: t('removeMemberFailed') }));
        setBusy(false);
      });
  };

  const tags = (member && member.tags) || [];
  const allocations = (member && member.allocations) || [];
  const tagMeta = useMemo(() => Object.fromEntries(
    catalog.map((c) => [c.tag, c]),
  ), [catalog]);

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          {member && member.photo_url && (
            <img className="bravos-brand__avatar" src={member.photo_url} alt="" />
          )}
          <div>
            <Title my="0" className="mt-1">{member ? member.name : t('loading')}</Title>
            {member && member.email && <p className="bravos-brand__name">{member.email}</p>}
          </div>
          <button
            type="button"
            className="bravos-wallet__link bravos-members__back"
            onClick={() => navigate('/company-members')}
          >
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {loading || !member ? (
        <Card><span className="auth__spinner" /></Card>
      ) : (
        <>
          <Card>
            <div className="bravos-member__row">
              {member.is_admin ? (
                <>
                  <span className="bravos-member__role">{t('roleAdmin')}</span>
                  {!member.is_contact && selfId && member.id !== selfId && (
                    <button
                      type="button"
                      className="bravos-member__makeadmin"
                      onClick={demoteAdmin}
                      disabled={busy}
                    >
                      {t('demoteFromAdmin')}
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="bravos-member__makeadmin"
                  onClick={makeAdmin}
                  disabled={busy}
                >
                  {t('makeAdmin')}
                </button>
              )}
              {isBravos && (
                <span className={`bravos-cta__tag ${member.is_member ? 'bravos-cta__tag--done' : ''}`}>
                  {member.is_member ? t('memberBadge') : t('notMemberBadge')}
                </span>
              )}
            </div>

            <p className="bravos-form__label">{t('tags')}</p>
            <datalist id="bravos-member-tag-options">
              {catalog.map((c) => <option key={c.tag} value={c.tag} />)}
            </datalist>
            <div className="bravos-tags">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="bravos-tag"
                  style={tagStyle((tagMeta[tag] || {}).color)}
                >
                  {(tagMeta[tag] || {}).icon && (
                    <Icon name={tagMeta[tag].icon} className="bravos-tag__icon" />
                  )}
                  {tag}
                  <button
                    type="button"
                    className="bravos-tag__x"
                    onClick={() => setTags(tags.filter((x) => x !== tag))}
                    disabled={busy}
                  >
                    ×
                  </button>
                </span>
              ))}
              {adding ? (
                <input
                  className="bravos-taginput"
                  autoFocus
                  list="bravos-member-tag-options"
                  value={tagValue}
                  maxLength={40}
                  placeholder={t('addTagPlaceholder')}
                  onChange={(e) => setTagValue(e.target.value)}
                  onBlur={commitTag}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitTag();
                    if (e.key === 'Escape') { setTagValue(''); setAdding(false); }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="bravos-tag__add"
                  onClick={() => setAdding(true)}
                  disabled={busy}
                >
                  + {t('addTag')}
                </button>
              )}
            </div>
            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </Card>

          {/* Lab seats are a Bravos thing: elsewhere the card would only ever say "none". */}
          {(isBravos || allocations.length > 0) && (
          <Card>
            <p className="bravos-form__label">{t('theirSeats')}</p>
            {allocations.length === 0 ? (
              <p className="text-muted mb-0">{t('noSeatsHeld')}</p>
            ) : (
              allocations.map((a) => (
                <div key={a.ticket_id} className="bravos-member__alloc">
                  <span className="bravos-member__alloc-name">
                    {a.event_name || t('lab')}
                    {a.type_name && <span className="bravos-member__alloc-type"> · {a.type_name}</span>}
                  </span>
                  <button
                    type="button"
                    className="bravos-member__reclaim"
                    onClick={() => reclaimSeat(a.ticket_id)}
                    disabled={reclaiming === a.ticket_id}
                  >
                    {reclaiming === a.ticket_id ? '…' : t('reclaimSeat')}
                  </button>
                </div>
              ))
            )}
          </Card>
          )}

          {/* Last, and on its own, because it is the one action here that cannot be undone from
              this screen. Never on your own row — you would lock yourself out of the company. */}
          {selfId && member.id !== selfId && (
            <Card>
              <button
                type="button"
                className="bravos-member__removebtn"
                onClick={removeMember}
                disabled={busy}
              >
                <Icon name="trash" className="me-1" /> {t('removeMember')}
              </button>
              <span className="bravos-netform__hint">{t('removeMemberNote')}</span>
            </Card>
          )}
        </>
      )}
    </Base>
  );
};

export default CompanyMemberPage;
